<template>
    <div class="spark-blocks-container">
        <div class="spark-blocks-section-title">
            <span>Block Categories</span>
            <span v-if="allBlocks.length > 0" class="badge bg-light text-muted border fw-normal">
                {{ allBlocks.length }} Library Blocks
            </span>
        </div>

        <!-- Block Grid Tiles -->
        <div class="spark-block-grid">
            <button
                v-for="(button, index) in visibleButtons"
                :key="index"
                type="button"
                class="spark-block-tile"
                :class="{
                    'spark-block-tile--active': selectedBlockLabel === button.label && button.label !== 'Pricing Table',
                    'spark-block-tile--builder': button.label === 'Pricing Table'
                }"
                :disabled="button.disabled"
                @click="handleClick(button.label)"
                :title="button.label === 'Pricing Table' ? 'Open interactive pricing table builder' : `Browse ${button.label} templates`"
            >
                <span v-if="button.label === 'Pricing Table'" class="spark-block-tile__badge bg-info text-white">
                    Builder
                </span>
                <span v-else-if="getBlockCount(button.label) > 0" class="spark-block-tile__badge bg-light text-muted border">
                    {{ getBlockCount(button.label) }}
                </span>
                <i :class="button.icon" class="spark-block-tile__icon"></i>
                <span class="spark-block-tile__label">{{ button.label }}</span>
            </button>
        </div>

        <!-- Testimonial Variable Field if active -->
        <div v-if="showTestimonial" class="mb-3">
            <testimonial-variable-field></testimonial-variable-field>
        </div>

        <!-- Template Variants Panel -->
        <div v-if="selectedBlockLabel && selectedBlockLabel !== 'Pricing Table' && !showTestimonial" class="spark-variants-panel">
            <div class="spark-variants-header">
                <h6 class="spark-variants-header__title">
                    <i class="fa-solid fa-folder-open text-primary"></i>
                    <span>{{ selectedBlockLabel }} Templates</span>
                    <span class="spark-pill-badge spark-pill-badge--primary ms-1">
                        {{ filteredVariableButtons.length }}
                    </span>
                </h6>
                <input
                    v-if="variableButtons.length > 2"
                    type="text"
                    v-model="searchQuery"
                    placeholder="Filter templates..."
                    class="spark-variant-search-input"
                />
            </div>

            <!-- Variants List -->
            <div v-if="filteredVariableButtons.length > 0" class="spark-variant-list">
                <div v-for="(button, index) in filteredVariableButtons" :key="index" class="spark-variant-card">
                    <div class="spark-variant-card__top">
                        <div class="spark-variant-title">
                            <span class="spark-variant-title__text">{{ button.label }}</span>
                            <span v-if="button.is_default" class="spark-badge-default spark-badge-default--blue">
                                <i class="fa-solid fa-star spark-badge-default__star"></i> Default
                            </span>
                        </div>
                    </div>
                    <div v-if="button.field_value" class="spark-variant-preview">
                        {{ stripHtmlPreview(button.field_value) }}
                    </div>
                    <div class="spark-variant-card__bottom">
                        <div class="spark-variant-actions">
                            <button
                                type="button"
                                class="spark-btn-action-replace"
                                @click="handleReplace(button.section_id, button.field_value)"
                                title="Replace the entire section in document with this template"
                            >
                                <i class="fa-solid fa-repeat"></i>
                                <span>Replace</span>
                            </button>
                            <button
                                type="button"
                                class="spark-btn-action-append"
                                @click="handleVariableClick(button.section_id, button.field_value)"
                                title="Append this template into the document section"
                            >
                                <i class="fa-solid fa-plus"></i>
                                <span>Append</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty Variants for this category -->
            <div v-else class="spark-blocks-empty">
                <div class="spark-blocks-empty__icon">
                    <i class="fa-regular fa-folder-open"></i>
                </div>
                <div class="spark-blocks-empty__title">
                    {{ searchQuery ? 'No matching templates' : 'No templates found' }}
                </div>
                <div class="spark-blocks-empty__desc">
                    {{ searchQuery ? `No templates matched "${searchQuery}".` : `No library templates available for ${selectedBlockLabel}.` }}
                </div>
            </div>
        </div>

        <!-- Initial Prompt when no block category is selected yet -->
        <div v-else-if="!showTestimonial" class="spark-blocks-empty border rounded bg-white">
            <div class="spark-blocks-empty__icon text-muted">
                <i class="fa-solid fa-cubes-stacked"></i>
            </div>
            <div class="spark-blocks-empty__title">Select a Block Category</div>
            <div class="spark-blocks-empty__desc">
                Choose a section category above to browse available library templates, or click <strong>Pricing Table</strong> to launch the interactive builder.
            </div>
        </div>

        <Teleport to="body">
            <PricingTableModal ref="pricingTableModal" :setItems="setItems" :isLoading="isLoading"
                @update-pricing-table="updatePricingTable" @save-proposal="saveProposal" @has-changes="handlePricingChanges"
                @sync-product-services="handleSyncProductServices" />
        </Teleport>

        <div class="row" style="margin-left: 0px; max-height: 850px; overflow-y: auto;" v-if="false">
            <div class="row">
                <button class="col-4 button-select continue" @click.stop="buildPricingTable()"
                    :disabled="!hasPricingTableChanges"><i class="fa-solid fa-wrench"></i> &nbsp; Build Table</button>
                <div class="col-4"></div>
                <button class="col-4 button-select continue" @click.stop="addPricingOption()"><i
                        class="fa-solid fa-table"></i> &nbsp; Add Option</button>
            </div>
            <div class="row">
                <br>
            </div>
            <div class="row">
                <table class="product-table">
                    <thead>
                        <tr>
                            <th>Product</th>
                            <th>Description</th>
                            <th>Notes</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Discount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in productList" :key="index">
                            <td class="product-name-cell" :title="item.product"
                                @click.stop="startEdit(index, 'product')">
                                <div style="display: flex; align-items: center; gap: 8px;">
                                    <i class="fa-solid fa-times remove-icon" title="Remove Product"
                                        @click.stop="removeProduct(index)"></i>
                                    <strong v-if="!item.isEditingProduct">{{ item.product }}</strong>
                                    <input v-else v-focus type="text" v-model="item.product" class="product-name-input"
                                        @click.stop
                                        @blur="endEdit(item, 'product')"
                                        @keyup.enter="endEdit(item, 'product')" />
                                </div>
                            </td>
                            <td :title="stripHtml(item.description || '')" @click.stop="openRichTextModal(item, 'description')" style="cursor: pointer; text-align: left;">
                                <span>{{ truncateText(stripHtml(item.description), 50) }}</span>
                            </td>
                            <td :title="stripHtml(item.remarks || '')" @click.stop="openRichTextModal(item, 'remarks')" style="cursor: pointer; text-align: left;">
                                <span>{{ truncateText(stripHtml(item.remarks), 50) }}</span>
                            </td>
                            <td style="text-align: center;" @click.stop="startEdit(index, 'quantity')">
                                <span v-if="!item.isEditingQuantity">{{ item.quantity }}</span>
                                <input v-else type="number" v-model.number="item.quantity" @blur="endEdit(item, 'quantity')"
                                    @keyup.enter="endEdit(item, 'quantity')" />
                            </td>
                            <td @click.stop="startEdit(index, 'pricing')">
                                <span v-if="!item.isEditingPricing">{{ formatCurrency(item.pricing) }}</span>
                                <input v-else type="number" v-model.number="item.pricing" @blur="endEdit(item, 'pricing')"
                                    @keyup.enter="endEdit(item, 'pricing')" />
                            </td>
                            <td @click.stop="startEdit(index, 'discount')">
                                <span v-if="!item.isEditingDiscount">{{ formatCurrency(item.discount) }}</span>
                                <input v-else type="number" v-model.number="item.discount" @blur="endEdit(item, 'discount')"
                                    @keyup.enter="endEdit(item, 'discount')" />
                            </td>
                        </tr>
                        <tr class="add-product-row">
                            <td @click="addProduct(productList.length)">
                                <span>+ Product</span>
                            </td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                        <tr v-if="isSelectingProduct">
                            <td colspan="6">
                                <div style="max-height: 300px; overflow-y: auto;">
                                    <table class="product-selection-table">
                                        <tbody>
                                            <tr v-for="(product, index) in availableProducts" :key="index">
                                                <td style="color: #00bcd4"><strong>{{ product.name }}</strong></td>
                                                <td>{{ formatCurrency(product.defaultPricing) }}</td>
                                                <td style="width: 50px; text-align: center; padding: 5px;">
                                                    <button class="button-select continue add-btn-small"
                                                        @click.stop="selectProduct(product)"><i
                                                            class="fa-solid fa-plus"></i></button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="pricingOptionCount > 1">
                <div v-for="(productOption, index) in productListOptions" :key="index">
                    <span class="orange-label-product-option" style="margin-left: -10px;">Product Pricing for Option {{
                        this.toOptionName(index + 2) }}</span>
                    <div class="row">
                        <div class="col-12" style="margin-left: -10px;">
                            <table class="product-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Description</th>
                                        <th>Notes</th>
                                        <th>Qty</th>
                                        <th>Price</th>
                                        <th>Discount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in productOption.productList" :key="index">
                                        <td class="product-name-cell" :title="item.product"
                                            @click.stop="startEditOption(productOption.productList, index, 'product')">
                                            <div style="display: flex; align-items: center; gap: 8px;">
                                                <i class="fa-solid fa-times remove-icon" title="Remove Product"
                                                    @click.stop="removeProductOption(productOption, productOption.productList, index)"></i>
                                                <strong v-if="!item.isEditingProduct">{{ item.product }}</strong>
                                                <input v-else v-focus type="text" v-model="item.product" class="product-name-input"
                                                    @click.stop
                                                    @blur="endEditOption(item, 'product')"
                                                    @keyup.enter="endEditOption(item, 'product')" />
                                            </div>
                                        </td>
                                        <td :title="stripHtml(item.description || '')"
                                            @click.stop="openRichTextModal(item, 'description')" style="cursor: pointer; text-align: left;">
                                            <span>{{ truncateText(stripHtml(item.description), 50) }}</span>
                                        </td>
                                        <td :title="stripHtml(item.remarks || '')"
                                            @click.stop="openRichTextModal(item, 'remarks')" style="cursor: pointer; text-align: left;">
                                            <span>{{ truncateText(stripHtml(item.remarks), 50) }}</span>
                                        </td>
                                        <td style="text-align: center;"
                                            @click.stop="startEditOption(productOption.productList, index, 'quantity')">
                                            <span v-if="!item.isEditingQuantity">{{ item.quantity }}</span>
                                            <input v-else type="number" v-model.number="item.quantity"
                                                @blur="endEditOption(item, 'quantity')"
                                                @keyup.enter="endEditOption(item, 'quantity')" />
                                        </td>
                                        <td @click.stop="startEditOption(productOption.productList, index, 'pricing')">
                                            <span v-if="!item.isEditingPricing">{{ formatCurrency(item.pricing) }}</span>
                                            <input v-else type="number" v-model.number="item.pricing"
                                                @blur="endEditOption(item, 'pricing')"
                                                @keyup.enter="endEdit(item, 'pricing')" />
                                        </td>
                                        <td @click.stop="startEditOption(productOption.productList, index, 'discount')">
                                            <span v-if="!item.isEditingDiscount">{{ formatCurrency(item.discount) }}</span>
                                            <input v-else type="number" v-model.number="item.discount"
                                                @blur="endEditOption(item, 'discount')"
                                                @keyup.enter="endEdit(item, 'discount')" />
                                        </td>
                                    </tr>
                                    <tr class="add-product-row">
                                        <td @click="addProductOption(productOption, productOption.productList.length)">
                                            <span>+ Product</span>
                                        </td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td colspan="2" style="text-align: end;" @click="removeOption(productOption)">
                                            <span>Remove Option</span>
                                        </td>
                                    </tr>
                                    <tr v-if="productOption.isSelectingProduct">
                                        <td colspan="6">
                                            <div style="max-height: 300px; overflow-y: auto;">
                                                <table class="product-selection-table">
                                                    <tbody>
                                                        <tr v-for="(product, index) in availableProducts" :key="index">
                                                            <td style="color: #00bcd4"><strong>{{ product.name }}</strong>
                                                            </td>
                                                            <td>{{ formatCurrency(product.defaultPricing) }}</td>
                                                            <td style="width: 50px; text-align: center; padding: 5px;">
                                                                <button class="button-select continue add-btn-small"
                                                                    @click.stop="selectProductOption(productOption, product)"><i
                                                                        class="fa-solid fa-plus"></i></button>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Rich Text Editing Modal -->
        <div v-if="showRichTextModal" class="rich-text-modal-backdrop">
          <div class="rich-text-modal">
            <div class="rich-text-modal-header">
              <h5>Edit {{ richTextEditingField === 'description' ? 'Description' : 'Notes' }}</h5>
              <button type="button" class="btn-close-custom" @click="closeRichTextModal">&times;</button>
            </div>
            <div class="rich-text-modal-body">
              <div class="rich-text-toolbar">
                <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('bold')" title="Bold">
                  <i class="fa-solid fa-bold"></i>
                </button>
                <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('italic')" title="Italic">
                  <i class="fa-solid fa-italic"></i>
                </button>
                <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('underline')" title="Underline">
                  <i class="fa-solid fa-underline"></i>
                </button>
                <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('insertUnorderedList')" title="Bullet List">
                  <i class="fa-solid fa-list-ul"></i>
                </button>
              </div>
              <div 
                ref="richTextEditor" 
                class="rich-text-editor" 
                contenteditable="true"
                @input="onEditorInput"
                style="text-align: left;"
              ></div>
              <div 
                class="rich-text-char-counter" 
                :style="{ color: richTextLength > 254 ? '#dc3545' : '#6c757d' }"
                v-if="richTextEditingField === 'remarks'"
              >
                {{ richTextLength }}/254
              </div>
            </div>
            <div class="rich-text-modal-footer">
              <button type="button" class="btn-cancel" @click="closeRichTextModal">Cancel</button>
              <button 
                type="button" 
                class="btn-save" 
                @click="saveRichTextContent"
                :disabled="richTextEditingField === 'remarks' && richTextLength > 254"
              >Save</button>
            </div>
          </div>
        </div>
    </div>
</template>

<script>
import 'vue-tel-input/dist/vue-tel-input.css'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss";
import TestimonialVariableField from '@/components/ContentLibrary/TestimonialVariableFields.vue';
import PricingTableModal from './PricingTableModal.vue';
import { log } from '@/plugins/helper.js'
import { isEmpty } from '../../plugins/helper';

export default {
    components: {
        TestimonialVariableField,
        PricingTableModal,
    },

    directives: {
        focus: {
            mounted(el) {
                el.focus();
            }
        }
    },

    emits: ['onSubmit', 'onDelete', 'onReady', 'pricingTableActive', 'pricingTableInActive', 'update-pricing-table', 'save-proposal', 'pricing-table-changes'],

    props: {
        item: {
            type: Object,
        },
        isHidden: {
            type: Boolean,
        },
        setItems: {
            type: Object,
        },
        isLoading: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            selectedBlockLabel: '',
            searchQuery: '',
            organisationVariableFields: [],
            organisations: [],
            rolesVariableFields: [],
            productVariableFields: [],
            userVariableFields: [],
            buttons: [
                { label: 'Cover Page', icon: 'fa-solid fa-file-lines', disabled: false, hidden: false },
                { label: 'Product / Service', icon: 'fa-solid fa-box-open', disabled: false, hidden: false },
                { label: 'Pricing Table', icon: 'fa-solid fa-hand-holding-dollar', disabled: false, hidden: false },
                { label: 'Image Grid', icon: 'fa-solid fa-images', disabled: false, hidden: false },
                { label: 'Testimonials', icon: 'fa-solid fa-comments', disabled: false, hidden: true },
                { label: 'Video', icon: 'fa-solid fa-photo-film', disabled: true, hidden: true },
                { label: 'Content', icon: 'fa-solid fa-feather', disabled: false, hidden: true },
                { label: 'Social Impact', icon: 'fa-solid fa-triangle-exclamation', disabled: false, hidden: false },
                { label: 'Compliance', icon: 'fa-solid fa-kit-medical', disabled: false, hidden: false },
                { label: 'WHS Responsibilities', icon: 'fa-solid fa-briefcase', disabled: false, hidden: true },
                { label: 'Terms & Condition', icon: 'fa-solid fa-file-signature', disabled: false, hidden: false },
                { label: 'Acceptance / Execution', icon: 'fa-solid fa-clipboard-check', disabled: false, hidden: true },
                { label: 'Organisation Table', icon: 'fa-solid fa-sitemap', disabled: false, hidden: true },
                { label: 'Next Steps', icon: 'fa-solid fa-angles-right', disabled: false, hidden: false },
            ],
            variableButtons: [],
            showTestimonial: false,
            showImageGridSettings: false,
            showPricingTableSettings: false,
            showRichTextModal: false,
            richTextEditingItem: null,
            richTextEditingField: '',
            richTextLength: 0,
            allBlocks: [],
            isSelectingProduct: false,
            pricingOptionCount: 1,

            rawAuthProducts: [],
            rawInternalPricing: [],
            availableProducts: [],
            tempAvailableProducts: [],
            productListOptions: [],
            products: [],
            productList: [],

            filteredProposalItems: [],
            newProductIndex: -1,
            isCharityLead: false,
            hasPricingTableChanges: false,
            hasPricingTableActive: false,
            defaultProductBlocks: [],
        }
    },

    computed: {
        visibleButtons() {
            return this.buttons.filter(b => !b.hidden);
        },

        filteredVariableButtons() {
            if (!this.searchQuery) return this.variableButtons;
            const q = this.searchQuery.toLowerCase().trim();
            return this.variableButtons.filter(b => 
                (b.label && b.label.toLowerCase().includes(q)) ||
                (b.field_value && b.field_value.toLowerCase().includes(q))
            );
        },

        calculateSubTotalPrice() {
            return this.productList.reduce((total, product) => {
                if (product.item_hidden) return total;
                const productTotal = (parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0);
                return (total + productTotal);
            }, 0).toFixed(2);
        },

        calculateTotalPrice() {
            return this.productList.reduce((total, product) => {
                if (product.item_hidden) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 1.1;
                return (total + productTotal);
            }, 0).toFixed(2);
        },

        calculateTotalGST() {
            return this.productList.reduce((total, product) => {
                if (product.item_hidden) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 0.1;
                return (total + productTotal);
            }, 0).toFixed(2);
        },
    },

    watch: {
        setItems: {
            immediate: true,
            deep: true,
            async handler(Val) {
                if (!isEmpty(Val) && !isEmpty(Val.items)) {
                    if (this.rawAuthProducts.length === 0 || this.rawInternalPricing.length === 0) {
                        await Promise.all([
                            this.getAuthProducts(),
                            this.getInternalPricing()
                        ]);
                        this.buildAvailableProductsList();
                    }

                    this.tempAvailableProducts = JSON.parse(JSON.stringify(this.availableProducts))
                    this.productList = []
                    this.productListOptions = []
                    this.pricingOptionCount = 1
                    this.convertLeadProductsToSelectedProducts(Val.items)
                    this.availableProducts = JSON.parse(JSON.stringify(this.tempAvailableProducts))
                }
            }
        },
    },

    created() {
        this.init()
    },

    mounted() {
        this.$emit('onReady')
    },

    methods: {
        truncateText(text, length = 50) {
            if (!text) return "";
            if (text.length <= length) return text;
            return text.substring(0, length) + "...";
        },
        stripHtml(html) {
            if (!html) return "";
            return html.replace(/<[^>]*>/g, "");
        },
        openRichTextModal(item, field) {
            this.richTextEditingItem = item;
            this.richTextEditingField = field;
            this.showRichTextModal = true;
            let val = item[field] || "";
            val = this.convertToHtml(val);
            this.richTextLength = val.length;
            this.$nextTick(() => {
                const editor = this.$refs.richTextEditor;
                if (editor) {
                    editor.innerHTML = val;
                    editor.focus();
                }
            });
        },
        convertToHtml(text) {
            if (!text) return "";
            if (/<[a-z][\s\S]*>/i.test(text)) {
                return text;
            }
            return text.replace(/\r?\n/g, "<br>");
        },
        execEditorCommand(command) {
            document.execCommand(command, false, null);
            this.updateLength();
        },
        updateLength() {
            const editor = this.$refs.richTextEditor;
            if (editor) {
                this.richTextLength = editor.innerHTML.length;
            }
        },
        onEditorInput(e) {
            this.richTextLength = e.target.innerHTML.length;
        },
        closeRichTextModal() {
            this.showRichTextModal = false;
            this.richTextEditingItem = null;
            this.richTextEditingField = '';
            this.richTextLength = 0;
        },
        saveRichTextContent() {
            const editor = this.$refs.richTextEditor;
            if (editor && this.richTextEditingItem) {
                let content = editor.innerHTML;
                if (content === '<br>' || content === '<p><br></p>' || content === '<div><br></div>') {
                    content = '';
                }
                this.richTextEditingItem[this.richTextEditingField] = content;
                this.hasPricingTableChanges = true;
            }
            this.closeRichTextModal();
        },

        handlePricingChanges(hasChanges) {
            this.hasPricingTableChanges = hasChanges;
            this.$emit('pricing-table-changes', hasChanges);
        },

        updatePricingTable(html) {
            this.$parent.setSectionContentById("start_pricing_table_section", html);
        },

        saveProposal(proposal_items) {
            this.syncProductServiceBlocks(proposal_items);
            this.$parent.saveContent(proposal_items);
        },

        calculateOptionSubtotalPrice(productList) {
            return productList.reduce((total, product) => {
                if (product.item_hidden) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0));
                return total + productTotal;
            }, 0).toFixed(2);
        },

        calculateOptionTotalPrice(productList) {
            return productList.reduce((total, product) => {
                if (product.item_hidden) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 1.1;
                return total + productTotal;
            }, 0).toFixed(2);
        },

        calculateOptionGST(productList) {
            return productList.reduce((total, product) => {
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 0.1;
                return total + productTotal;
            }, 0).toFixed(2);
        },

        toOptionName(count) {
            if (count <= 1) {
                return '';
            }
            let result = '';
            let num = count;
            while (num > 0) {
                let remainder = num % 26;
                if (remainder === 0) {
                    remainder = 26;
                }
                result = String.fromCharCode(64 + remainder) + result;
                num = Math.floor((num - remainder) / 26);
            }
            return result;
        },

        isUUID(uuid) {
            if (typeof uuid !== 'string') return false;
            if (uuid.length !== 36) return false;
            if (uuid[8] !== '-' || uuid[13] !== '-' || uuid[18] !== '-' || uuid[23] !== '-') return false;
            return true;
        },

        async fetchDefaultBlocks() {
            try {
                const response = await this.$axios.get("/blocks/default");
                this.defaultProductBlocks = response.data.blocks || [];
            } catch (error) {
                console.error("Failed to fetch default blocks", error);
            }
        },

        syncProductServiceBlocks(proposal_items) {
            if (!proposal_items) return;

            // 1. Build a canonical set of all active product IDs from the pricing table,
            //    expanding each to include both auth code and internal UUID forms.
            const activeProductIds = new Set();
            const uniqueProductIds = [...new Set(proposal_items.map(p => p.product_id))];

            uniqueProductIds.forEach(productId => {
                if (!productId) return;
                activeProductIds.add(productId.toString());

                // Expand: auth code → internal UUID(s)
                this.rawInternalPricing
                    .filter(ip => ip.HasAuthProductLinked == 1 && ip.AuthItemCode == productId)
                    .forEach(ip => activeProductIds.add(ip.ProductID.toString()));

                // Expand: internal UUID → auth code
                this.rawInternalPricing
                    .filter(ip => ip.HasAuthProductLinked == 1 && ip.ProductID == productId)
                    .forEach(ip => activeProductIds.add(ip.AuthItemCode.toString()));
            });

            // 2. Find matching blocks from our fetched default blocks (block-centric)
            let blocksToKeep = [];
            let activeIds = [];

            this.defaultProductBlocks.forEach(db => {
                if (db.BlockType && db.BlockType.name === "ProductService" && db.products && db.products.length > 0) {
                    // Check if ANY product on this block matches the canonical set
                    const hasMatch = db.products.some(p => {
                        const pid = p.product_id?.toString() || '';
                        const pUuid = p.Product?.id?.toString() || '';
                        return (pid && activeProductIds.has(pid)) || (pUuid && activeProductIds.has(pUuid));
                    });
                    
                    if (hasMatch && !isEmpty(db.content)) {
                        const blockId = `product_service_${db.id}`;
                        if (!activeIds.includes(blockId)) {
                            activeIds.push(blockId);
                            blocksToKeep.push({
                                id: blockId,
                                content: `<div id='${blockId}'><br>${db.content}</div>`
                            });
                        }
                    }
                }
            });

            // 4. Safely update the Editor Content — use a single atomic DOM write
            //    to avoid race conditions where checkIfIDExist queries the live editor
            //    before it has flushed a prior setSectionContentById update.
            let currentHtml = this.$parent.getSectionContentById("start_product_services_section");
            if (!currentHtml) currentHtml = ""; // Fallback if empty
            
            let parser = new DOMParser();
            let doc = parser.parseFromString(currentHtml, 'text/html');

            // Scrub blocks that are no longer active
            let existingBlocks = doc.querySelectorAll("div[id^='product_service_']");
            let htmlChanged = false;

            existingBlocks.forEach(block => {
                if (!activeIds.includes(block.id)) {
                    block.remove(); // The item was removed from the pricing table
                    htmlChanged = true;
                }
            });

            // Append any new blocks that aren't already in the parsed doc
            let existingIdsInDoc = new Set(
                Array.from(doc.querySelectorAll("div[id^='product_service_']")).map(el => el.id)
            );

            blocksToKeep.forEach(block => {
                if (!existingIdsInDoc.has(block.id)) {
                    // Parse the block HTML and append its nodes to the doc body
                    let blockDoc = parser.parseFromString(block.content, 'text/html');
                    Array.from(blockDoc.body.childNodes).forEach(node => {
                        doc.body.appendChild(node.cloneNode(true));
                    });
                    existingIdsInDoc.add(block.id);
                    htmlChanged = true;
                }
            });

            // Write back once — single atomic update
            if (htmlChanged) {
                this.$parent.setSectionContentById("start_product_services_section", doc.body.innerHTML);
            }
        },

        async init() {
            this.getAllVariableFieldBlocks();
            this.getOrganisationVariableFields();
            this.getAuthOrganisations();
            await Promise.all([
                this.getAuthProducts(),
                this.getInternalPricing(),
                this.fetchDefaultBlocks()
            ]);
            this.buildAvailableProductsList();
        },

        async getAuthProducts() {
            await this.$axios.get("/auth-products")
                .then((response) => {
                    this.rawAuthProducts = response.data.authProducts || [];
                })
                .catch((error) => console.error(error));
        },

        async getInternalPricing() {
            await this.$axios.get("/get-product-pricing")
                .then((response) => {
                    this.rawInternalPricing = response.data.productPrices || [];
                })
                .catch((error) => console.error(error));
        },

        buildAvailableProductsList() {
            this.availableProducts = [];
            this.rawAuthProducts.forEach(authItem => {
                if (!authItem.Product) return;

                let price = authItem.Product.price ?? authItem.Product.Price ?? authItem.price ?? authItem.Price ?? 0;
                let description = authItem.Product.description ?? authItem.Product.Description ?? authItem.description ?? "No Product Description";
                let name = authItem.Product.display_name ?? "No Name";
                let isCharity = authItem.is_charity_product === true || authItem.is_charity_product == 1;

                this.availableProducts.push({
                    id: authItem.Product.id,
                    name: name,
                    defaultPricing: price,
                    description: description,
                    defaultQuantity: 1,
                    isCharityProduct: isCharity,
                });
            });
            this.availableProducts.sort((a, b) => a.name.localeCompare(b.name));
        },

        stripHtmlPreview(html) {
            if (!html) return '';
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            const text = tmp.textContent || tmp.innerText || '';
            return text.trim();
        },

        getBlockCount(clickLabel) {
            if (!this.allBlocks || this.allBlocks.length === 0) return 0;
            if (clickLabel === "Cover Page") {
                return this.allBlocks.filter(v => v.BlockType?.name === "CoverPage" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Product / Service") {
                return this.allBlocks.filter(v => v.BlockType?.name === "ProductService" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Social Impact") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Content" && v.content_type?.name === "SocialImpact" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Compliance") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Content" && v.content_type?.name === "CommitmentToSafetyAndCompliance" && !isEmpty(v.content)).length;
            } else if (clickLabel === "WHS Responsibilities") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Content" && v.content_type?.name === "WHSResponsibilities" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Terms & Condition") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Content" && v.content_type?.name === "TermsAndConditions" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Image Grid") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Image" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Next Steps") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Content" && v.content_type?.name === "NextSteps" && !isEmpty(v.content)).length;
            }
            return 0;
        },

        handleClick(clickLabel) {
            this.selectedBlockLabel = clickLabel;
            this.searchQuery = '';
            if (clickLabel == "Cover Page") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "CoverPage" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_cover_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Testimonials") {
                this.variableButtons = []
                this.showTestimonial = true
                this.showPricingTableSettings = false
                this.hasPricingTableActive = false
                this.$emit("pricingTableInActive")
                return
            } else if (clickLabel == "Pricing Table") {
                this.$refs.pricingTableModal.openModal();
                return
            } else if (clickLabel == "Product / Service") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "ProductService" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_product_services_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Social Impact") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Content" && vfield.content_type != undefined && vfield.content_type.name == "SocialImpact" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_content_social_impact_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Compliance") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Content" && vfield.content_type != undefined && vfield.content_type.name == "CommitmentToSafetyAndCompliance" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_content_commitment_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "WHS Responsibilities") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Content" && vfield.content_type != undefined && vfield.content_type.name == "WHSResponsibilities" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_content_whsr_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Terms & Condition") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Content" && vfield.content_type != undefined && vfield.content_type.name == "TermsAndConditions" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_content_terms_condition_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Image Grid") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Image" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_image_grid_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Next Steps") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Content" && vfield.content_type != undefined && vfield.content_type.name == "NextSteps" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_content_next_steps_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            }


            this.$emit("pricingTableInActive")
            this.hasPricingTableActive = false
            this.showTestimonial = false
            this.showPricingTableSettings = false
        },

        handleVariableClick(section_id, variable_value) {
            let resolved = this.$parent.replaceVariableFields ? this.$parent.replaceVariableFields(variable_value) : variable_value;
            this.$parent.appendToSectionContentById(section_id, resolved)
        },

        handleReplace(section_id, variable_value) {
            let resolved = this.$parent.replaceVariableFields ? this.$parent.replaceVariableFields(variable_value) : variable_value;
            this.$parent.setSectionContentById(section_id, resolved)
        },

        GetOrganisationName(org_id) {
            const organisation = this.organisations.find(org => org.id === org_id);
            return organisation ? organisation.name : null;
        },

        async getAllVariableFieldBlocks() {
            await this.$axios.get("/blocks/all?blockUse=Proposal")
                .then((response) => {
                    this.allBlocks = response.data.blocks
                    log(this.allBlocks)
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        async getProductVariableFields() {
            await this.$axios.get("/categories?name=ProductVariableFields")
                .then((response) => {
                    this.productVariableFields = response.data.category.subcategories
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        async getRolesVariableFields() {
            await this.$axios.get("/categories?name=RolesVariableFields")
                .then((response) => {
                    this.rolesVariableFields = response.data.category.subcategories
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        async getOrganisationVariableFields() {
            await this.$axios.get("/categories?name=OrganisationVariableFields")
                .then((response) => {
                    this.organisationVariableFields = response.data.category.subcategories
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        async getUserVariableFields() {
            await this.$axios.get("/categories?name=UserVariableFields")
                .then((response) => {
                    this.userVariableFields = response.data.category.subcategories
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        async getAuthOrganisations() {
            await this.$axios_auth.get("/vertex/organisations")
                .then((response) => {
                    this.organisations = response.data.organisations
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        formatCurrency(value) {
            const numericValue = parseFloat(value);
            if (isNaN(numericValue)) {
                return '$0.00';
            }
            return `$${numericValue.toFixed(2)}`;
        },
        calculateTotal(item) {
            return (item.quantity * item.pricing) - (item.discount * item.quantity);
        },
        calculateTotalDiscount(item) {
            return (item.discount * item.quantity);
        },
        addProduct(index) {
            this.isSelectingProduct = !this.isSelectingProduct;
            this.newProductIndex = index;
            this.hasPricingTableChanges = true;
        },

        addProductOption(productOption, index) {
            productOption.isSelectingProduct = !productOption.isSelectingProduct;
            productOption.newProductIndex = index;
            this.hasPricingTableChanges = true;
        },

        startEdit(index, field) {
            this.productList.forEach((item, i) => {
                if (i === index) {
                    item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = true;
                    item['current_product'] = item.product
                    item['current_qty'] = item.quantity
                    item['current_price'] = item.pricing
                    item['current_discount'] = item.discount
                    item['current_remarks'] = item.remarks
                    item['current_description'] = item.description
                } else {
                    item.isEditingProduct = false;
                    item.isEditingQuantity = false;
                    item.isEditingPricing = false;
                    item.isEditingDiscount = false;
                    item.isEditingRemarks = false;
                    item.isEditingDescription = false;
                }
            });
        },

        startEditOption(productList, index, field) {
            productList.forEach((item, i) => {
                if (i === index) {
                    item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = true;
                    item['current_product'] = item.product
                    item['current_qty'] = item.quantity
                    item['current_price'] = item.pricing
                    item['current_discount'] = item.discount
                    item['current_remarks'] = item.remarks
                    item['current_description'] = item.description
                } else {
                    item.isEditingProduct = false;
                    item.isEditingQuantity = false;
                    item.isEditingPricing = false;
                    item.isEditingDiscount = false;
                    item.isEditingRemarks = false;
                    item.isEditingDescription = false;
                }
            });

        },

        endEdit(item, field) {
            item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = false;
            if (item["current_product"] != item["product"] || item["current_qty"] != item["quantity"] || item["current_price"] != item["pricing"] || item["current_discount"] != item["discount"] || item["current_remarks"] != item["remarks"] || item["current_description"] != item["description"]) {
                this.hasPricingTableChanges = true
            }
        },

        endEditOption(item, field) {
            item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = false;
            if (item["current_product"] != item["product"] || item["current_qty"] != item["quantity"] || item["current_price"] != item["pricing"] || item["current_discount"] != item["discount"] || item["current_remarks"] != item["remarks"] || item["current_description"] != item["description"]) {
                this.hasPricingTableChanges = true
            }
        },

        selectProduct(selectedProduct) {
            const existingProductIndex = this.productList.findIndex(
                (item) => item.id === selectedProduct.id
            );

            const isCharityFree = selectedProduct.isCharityProduct && this.isCharityLead;
            const pricingToUse = isCharityFree ? 0 : (selectedProduct.defaultPricing || 0.00);
            const discountToUse = isCharityFree ? 0 : (selectedProduct.defaultDiscount || 0.00);

            if (existingProductIndex > -1) {
                this.productList.splice(existingProductIndex + 1, 0, {
                    id: selectedProduct.id,
                    product: selectedProduct.name,
                    description: this.convertToHtml(selectedProduct.description || "No Product Description"),
                    remarks: (selectedProduct.remarks) ? selectedProduct.remarks : "",
                    quantity: selectedProduct.defaultQuantity || 1,
                    discount: discountToUse,
                    pricing: pricingToUse,
                    isEditingProduct: false,
                    isEditingDescription: false,
                    isEditingRemarks: false,
                    isEditingQuantity: false,
                    isEditingPricing: false,
                    isEditingDiscount: false
                });
            } else {
                this.productList.push({
                    id: selectedProduct.id,
                    product: selectedProduct.name,
                    description: this.convertToHtml(selectedProduct.description || "No Product Description"),
                    remarks: (selectedProduct.remarks) ? selectedProduct.remarks : "",
                    quantity: selectedProduct.defaultQuantity || 1,
                    discount: discountToUse,
                    pricing: pricingToUse,
                    isEditingProduct: false,
                    isEditingDescription: false,
                    isEditingRemarks: false,
                    isEditingQuantity: false,
                    isEditingPricing: false,
                    isEditingDiscount: false
                });
            }

            this.isSelectingProduct = false;
            this.newProductIndex = -1;
            this.hasPricingTableChanges = true;
        },

        selectProductOption(productOption, selectedProduct) {
            const existingProductIndex = productOption.productList.findIndex(
                (item) => item.id === selectedProduct.id
            );

            const isCharityFreeOpt = selectedProduct.isCharityProduct && this.isCharityLead;
            const pricingToUseOpt = isCharityFreeOpt ? 0 : (selectedProduct.defaultPricing || 0.00);
            const discountToUseOpt = isCharityFreeOpt ? 0 : (selectedProduct.defaultDiscount || 0.00);

            if (existingProductIndex > -1) {
                productOption.productList.splice(existingProductIndex + 1, 0, {
                    id: selectedProduct.id,
                    product: selectedProduct.name,
                    description: this.convertToHtml(selectedProduct.description || "No Product Description"),
                    remarks: (selectedProduct.remarks) ? selectedProduct.remarks : "",
                    quantity: selectedProduct.defaultQuantity || 1,
                    discount: discountToUseOpt,
                    pricing: pricingToUseOpt,
                    isEditingProduct: false,
                    isEditingDescription: false,
                    isEditingRemarks: false,
                    isEditingQuantity: false,
                    isEditingPricing: false,
                    isEditingDiscount: false
                });
            } else {
                productOption.productList.push({
                    id: selectedProduct.id,
                    product: selectedProduct.name,
                    description: this.convertToHtml(selectedProduct.description || "No Product Description"),
                    remarks: (selectedProduct.remarks) ? selectedProduct.remarks : "",
                    quantity: selectedProduct.defaultQuantity || 1,
                    discount: discountToUseOpt,
                    pricing: pricingToUseOpt,
                    isEditingProduct: false,
                    isEditingDescription: false,
                    isEditingRemarks: false,
                    isEditingQuantity: false,
                    isEditingPricing: false,
                    isEditingDiscount: false
                });
            }

            productOption.isSelectingProduct = false;
            productOption.newProductIndex = -1;
            this.hasPricingTableChanges = true;
        },

        removeProductOption(productOption, productList, indexToRemove) {
            if (productList.length == 1) {
                this.removeOption(productOption)
                return
            }
            if (indexToRemove > -1) {
                productList.splice(indexToRemove, 1);
            }
            this.hasPricingTableChanges = true;
        },

        removeProduct(indexToRemove) {
            if (this.productList.length == 1) {
                alert("Cannot remove last product on table. Please add another one for replacement.")
                return
            }
            if (indexToRemove > -1) {
                this.productList.splice(indexToRemove, 1);
            }
            this.hasPricingTableChanges = true;
        },

        convertLeadProductsToSelectedProducts(products_array) {
            if (!isEmpty(products_array) && products_array.length > 0) {
                products_array.forEach((p) => {
                    let targetAuthId = p.product_id;

                    // If it is an old Internal ID (UUID), map it to Auth. Drop it if unlinked.
                    if (this.isUUID(targetAuthId)) {
                        const internalProductLink = this.rawInternalPricing.find(ip => ip.ProductID == targetAuthId);
                        if (internalProductLink && internalProductLink.HasAuthProductLinked == 1 && internalProductLink.AuthItemCode) {
                            targetAuthId = internalProductLink.AuthItemCode;
                        } else {
                            return; // Ignore internal items without a link
                        }
                    }

                    let productFromList = this.availableProducts.find(item => item.id == targetAuthId);
                    if (!productFromList) return;

                    this.isCharityLead = p.is_charity;

                    let customProductName = p.product_name || p.product || "";
                    if (!customProductName && p.Product && p.Product.display_name) {
                        customProductName = p.Product.display_name;
                    }

                    const newProduct = {
                        id: productFromList.id,
                        product: customProductName || productFromList.name,
                        description: this.convertToHtml(p.description || productFromList.description || "No Product Description"),
                        remarks: p.remarks || "",
                        quantity: p.quantity || 1,
                        discount: p.discount !== undefined ? p.discount : 0.00,
                        pricing: p.price !== undefined ? p.price : (productFromList.defaultPricing || 0.00),
                        isChild: !!(productFromList.parentIds && productFromList.parentIds.length > 0) || 
                                 p.is_child === true || p.is_child === 1 || String(p.is_child) === "true" ||
                                 p.isChild === true || p.isChild === 1 || String(p.isChild) === "true",
                        parentIds: productFromList.parentIds,
                        uiParentId: Date.now() + Math.random(), // Temp assign, will be aligned below
                        isEditingProduct: false,
                        isEditingDescription: false,
                        isEditingRemarks: false,
                        isEditingQuantity: false,
                        isEditingPricing: false,
                        isEditingDiscount: false
                    };

                    if (p.option_index == 1) {
                        this.productList.push(newProduct);
                    } else if (p.option_index >= 2) {
                        if (isEmpty(this.productListOptions[p.option_index - 2])) {
                            this.productListOptions.push({
                                productList: [],
                                isSelectingProduct: false,
                                newProductIndex: -1
                            })
                            this.pricingOptionCount++
                        }
                        this.productListOptions[p.option_index - 2].productList.push(newProduct);
                    }
                    this.hasPricingTableChanges = false;
                })

                // Re-align children to their parents loaded from DB so they sync together
                // Uses a proximity-based search to correctly handle duplicate parents
                const alignUiParents = (list) => {
                    let lastSeenParents = {}; // productId -> uiParentId
                    list.forEach(item => {
                        if (!item.isChild) {
                            lastSeenParents[item.id] = item.uiParentId;
                        } else if (item.parentIds && item.parentIds.length > 0) {
                            const matchingParentId = item.parentIds.find(pid => lastSeenParents[pid]);
                            if (matchingParentId) {
                                item.uiParentId = lastSeenParents[matchingParentId];
                            }
                        }
                    });
                };
                alignUiParents(this.productList);
                this.productListOptions.forEach(opt => {
                    if (opt.productList) alignUiParents(opt.productList);
                });
            }
        },

        addPricingOption() {
            let pO = [];
            if (!isEmpty(this.productList)) {
                this.productList.forEach(p => {
                    pO.push(JSON.parse(JSON.stringify(p)))
                })
            }
            this.productListOptions.push({
                productList: pO,
                isSelectingProduct: false,
                newProductIndex: -1
            })
            this.pricingOptionCount += 1
            this.hasPricingTableChanges = true;
        },

        removeOption(productOption) {
            let index = this.productListOptions.indexOf(productOption)
            this.productListOptions.splice(index, 1)
            this.hasPricingTableChanges = true;
        },

        buildPricingTable() {
    //         let productsToPropagate = ""
    //         let optionsToPropagate = ""

    //         this.productList.forEach(p => {
    //             productsToPropagate +=
    //                 `<tr id='product_list_${p.id}'>
    //                    <td style="border-color: black; border-width: 1px; text-align: center;">${p.product}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.description || "No Product Description"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.remarks || "No Remarks"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.quantity}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(p.pricing)}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(p.discount)}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalDiscount(p))}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotal(p))}</td>
    //                 </tr>
    //             `})

    //         productsToPropagate += `
    //                 <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;"><b>Subtotal<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateSubTotalPrice)}</td>
    //   </tr>
    //   <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td title="Goods and Services Tax" style="border-color: black; border-width: 1px; text-align: center;"><b>GST (10%)<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalGST)}</td>
    //   </tr>
    //   <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //        <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;"><b>Total AUD<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalPrice)}</td>
    //   </tr>
    //             `
    //         if (!isEmpty(this.productListOptions))
    //             this.productListOptions.forEach((plo, index) => {
    //                 let optionBody = ""

    //                 if (!isEmpty(plo.productList)) {
    //                     plo.productList.forEach(p => {
    //                         optionBody +=
    //                             `<tr id='product_list_${p.id}'>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${p.product}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.description || "No Product Description"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.remarks || "No Remarks"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.quantity}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(p.pricing)}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(p.discount)}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalDiscount(p))}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotal(p))}</td>
    //                 </tr>
    //             `})

    //                     optionBody += `
    //                  <tr>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;"><b>Subtotal<b></td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateOptionSubtotalPrice(plo.productList))}</td>
    //                 </tr>
    //                 <tr>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td title="Goods and Services Tax" style="border-color: black; border-width: 1px; text-align: center;"><b>GST (10%)<b></td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateOptionGST(plo.productList))}</td>
    //                 </tr>
    //                 <tr>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;"><b>Total AUD<b></td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateOptionTotalPrice(plo.productList))}</td>
    //                 </tr>
    //             `

    //                     optionsToPropagate += `
                
    //             <br>
    //             <span class="orange-label-product-option" style="margin-left: 0;">Pricing for Option ${this.toOptionName(index + 2)}</span>
    //             <table style="border-collapse: collapse; width: 100%; border-color: black; border: 2px solid black;" border="1">
    //             <thead>
    //                 <tr>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Product</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Description</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Notes</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Qty</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Price</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Per Item Discount</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total Discount</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total AUD</th>
    //                 </tr>
    //             </thead>
    //             <tbody>
    //                 ${optionBody}
    //             </tbody>
    //             </table>
    //             `
    //                 }
    //             })

    //         this.$parent.setSectionContentById("start_pricing_table_section", `<table style="border-collapse: collapse; width: 100%; border-color: black; border: 2px solid black;" border="1">
    //             <thead>
    //                 <tr>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Product</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Description</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Notes</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Qty</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Price</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Per Item Discount</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total Discount</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total AUD</th>
    //                 </tr>
    //             </thead>
    //             <tbody>
    //                 ${productsToPropagate}
    //             </tbody>
    //             </table>
    //                 ${(!isEmpty(optionsToPropagate) ? optionsToPropagate : "")}
    //         `)

            this.hasPricingTableChanges = false;

            let proposal_items = []
            this.productList.forEach((p) => {
                proposal_items.push({
                    product_id: p.id.toString(),
                    product_name: p.product,
                    description: p.description,
                    price: p.pricing,
                    quantity: p.quantity,
                    discount: p.discount,
                    option_index: 1,
                    remarks: p.remarks
                })
            })

            if (!isEmpty(this.productListOptions))
                this.productListOptions.forEach((plo, index) => {
                    if (!isEmpty(plo.productList)) {
                        plo.productList.forEach((p) => {
                            proposal_items.push({
                                product_id: p.id.toString(),
                                product_name: p.product,
                                description: p.description,
                                price: p.pricing,
                                quantity: p.quantity,
                                discount: p.discount,
                                option_index: index + 2,
                                remarks: p.remarks
                            })
                        })
                    }
                })

            this.syncProductServiceBlocks(proposal_items);
            this.$parent.saveContent(proposal_items, true)
        },

        handleSyncProductServices(payload) {
            const { append, activeIds } = payload;

            // Use a single atomic DOM write to avoid race conditions
            let currentHtml = this.$parent.getSectionContentById("start_product_services_section");
            if (!currentHtml) currentHtml = "";

            let parser = new DOMParser();
            let doc = parser.parseFromString(currentHtml, 'text/html');
            let htmlChanged = false;

            // 1. Scrub blocks that are no longer active
            let existingBlocks = doc.querySelectorAll("div[id^='product_service_']");
            existingBlocks.forEach(block => {
                if (!activeIds.includes(block.id)) {
                    block.remove();
                    htmlChanged = true;
                }
            });

            // 2. Append any new blocks that aren't already in the parsed doc
            if (append && append.length > 0) {
                let existingIdsInDoc = new Set(
                    Array.from(doc.querySelectorAll("div[id^='product_service_']")).map(el => el.id)
                );

                append.forEach(block => {
                    if (!existingIdsInDoc.has(block.id)) {
                        let blockDoc = parser.parseFromString(block.content, 'text/html');
                        Array.from(blockDoc.body.childNodes).forEach(node => {
                            doc.body.appendChild(node.cloneNode(true));
                        });
                        existingIdsInDoc.add(block.id);
                        htmlChanged = true;
                    }
                });
            }

            // Write back once — single atomic update
            if (htmlChanged) {
                this.$parent.setSectionContentById("start_product_services_section", doc.body.innerHTML);
            }
        },
    },
};

</script>

<style scoped>
/* form start */
form label:not(#isCharityLbl) {
    color: #00A0C1;
    margin: 0.5rem 0;
}

input:not([type="checkbox"]),
select {
    height: 40px;
    border-radius: 10px;
    max-width: 100px;
    border: 1px solid #D9D9D9;
    color: #435666;
}

.form--check-input {
    width: 12px;
    height: 12px;
    margin-left: -17px;
    margin-right: 10px;
}

/* form end */
.btn {
    width: 7rem;
}

.add-space {
    margin-bottom: 225px;
}

.button-group {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 10px;
}

.button-group-variable {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: 5px;
    padding: 10px;
    overflow-y: auto;
    scrollbar-color: #00A0C1 lightblue;
    max-height: clamp(300px, calc(100vh - 300px), 900px);
    overflow-x: hidden;
}

.block-button-container {
    display: flex;
    margin-left: -3px;
    gap: 3px;
}

@media (min-width: 1900px) {
    .block-button-container {
        gap: 6px;
    }
}

/* pricing table */
.product-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
    border: 1px solid #ddd;
}

.product-table th,
.product-table td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

.product-table th {
    background-color: #00bcd4;
    color: white;
    font-weight: bold;
}

.product-table tr:last-child td {
    border-bottom: none;
}

.product-table td:first-child {
    align-items: center;
}

.product-table td:first-child>span {
    margin-right: 5px;
}

.add-product-row td {
    color: #00bcd4;
    cursor: pointer;
}

.remove-icon {
    color: #dc3545;
    cursor: pointer;
    font-size: 1.1em;
    transition: transform 0.2s, color 0.2s;
}

.remove-icon:hover {
    color: #a71d2a;
    transform: scale(1.2);
}

.add-product-row td:first-child {
    font-weight: bold;
}

.add-product-row td:last-child {
    font-weight: bold;
    color: #ff8c00
}

.product-selection-list {
    background-color: #f9f9f9;
    border: 1px solid #ddd;
    padding: 10px;
    margin-top: 5px;
    border-radius: 5px;
}

.product-item {
    padding: 8px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.product-item:hover {
    background-color: #eee;
}

.product-table td input[type="number"] {
    width: 70px;
    padding: 6px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.9em;
    text-align: left;
}

.product-table td input[type="number"]:focus {
    outline: none;
    border-color: #00bcd4;
    box-shadow: 0 0 5px rgba(0, 188, 212, 0.5);
}

.product-selection-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
}

.product-selection-table th,
.product-selection-table td {
    padding: 8px;
    border: 1px solid #ddd;
    text-align: left;
}

.product-selection-table th {
    background-color: #f2f2f2;
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #f2f2f2;
}

.product-item {
    padding: 0;
    margin: 0;
    border: none;
    cursor: pointer;
}

.total-row {
    font-weight: bold;
    background-color: #f9f9f9;
}

.total-label {
    text-align: right;
    padding-right: 8px;
}

.total-amount {
    text-align: right;
}

.continue {
    background-color: #00bcd4;
    color: white;
}

.continue:disabled {
    background-color: lightgray;
    color: white;
}

.actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.button-select {
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: bold;
}

.add-btn-small {
    padding: 6px 12px;
    font-size: 0.9em;
}

.orange-label-product-option {
    display: inline-block;
    background-color: #ff8c00;
    color: white;
    padding: 6px 12px;
    border-radius: 5px;
    font-weight: bold;
    font-size: 0.95em;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.char-counter {
    display: block;
    text-align: right;
    font-size: 0.75rem;
    color: #6c757d;
    margin-top: 2px;
}

.product-search-input {
    width: 100%;
    padding: 10px;
    margin-bottom: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
}

.nav-tabs .nav-link {
    cursor: pointer;
    color: black;
}

.nav-tabs .nav-link.active {
    background-color: #00A0C1;
    color: #fff !important;
    border-color: #00A0C1;
}

.remove-tab-icon {
    margin-left: 10px;
    cursor: pointer;
    font-weight: bold;
}

.modal-custom-width {
    max-width: 95vw !important;
}

.blur-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(5px);
    background-color: rgba(0, 0, 0, 0.4);
}

/* Rich Text Modal Editor Styles */
.rich-text-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(5px);
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1060;
    display: flex;
    align-items: center;
    justify-content: center;
}
.rich-text-modal {
    background: #fff;
    border-radius: 12px;
    width: 600px;
    max-width: 90vw;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: fadeIn 0.2s ease-out;
}
.rich-text-modal-header {
    background-color: #00A0C1;
    color: white;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.rich-text-modal-header h5 {
    margin: 0;
    font-weight: 600;
    font-size: 1.15rem;
}
.btn-close-custom {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;
    opacity: 0.8;
    transition: opacity 0.2s;
}
.btn-close-custom:hover {
    opacity: 1;
}
.rich-text-modal-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.rich-text-toolbar {
    display: flex;
    gap: 6px;
    background: #f1f3f5;
    padding: 8px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
}
.toolbar-btn {
    background: white;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 0.9rem;
    cursor: pointer;
    color: #495057;
    transition: all 0.2s;
}
.toolbar-btn:hover {
    background: #e9ecef;
    border-color: #adb5bd;
    color: #212529;
}
.rich-text-editor {
    border: 1px solid #ced4da;
    border-radius: 8px;
    padding: 12px;
    min-height: 150px;
    max-height: 300px;
    overflow-y: auto;
    font-size: 0.95rem;
    line-height: 1.6;
    color: #333;
    outline: none;
    background: white;
    text-align: left;
}
.rich-text-editor:focus {
    border-color: #00A0C1;
    box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}
.rich-text-editor ul {
    margin-top: 0;
    margin-bottom: 8px;
    padding-left: 20px;
    list-style-type: disc !important;
}
.rich-text-editor li {
    margin-bottom: 4px;
}
.rich-text-char-counter {
    text-align: right;
    font-size: 0.8rem;
    color: #6c757d;
}
.rich-text-modal-footer {
    padding: 16px 20px;
    background: #f8f9fa;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}
.btn-cancel {
    background: white;
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 8px 16px;
    font-weight: 600;
    cursor: pointer;
    color: #495057;
    transition: all 0.2s;
}
.btn-cancel:hover {
    background: #f1f3f5;
    border-color: #adb5bd;
}
.btn-save {
    background: #00bcd4;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 8px 20px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
}
.btn-save:hover {
    background: #00a5bb;
}
.product-name-cell {
    cursor: pointer;
}
.product-name-input {
    width: 100%;
    min-width: 120px;
    padding: 4px 8px;
    border: 1px solid #00bcd4;
    border-radius: 4px;
    font-size: 0.9em;
    font-weight: bold;
    outline: none;
    box-shadow: 0 0 5px rgba(0, 188, 212, 0.4);
}
</style>